import { useEffect, useState } from 'react'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Cloud, Crown, Route as RouteIcon, ShoppingCart } from 'lucide-react'
import {
  emailSignIn,
  emailSignUp,
  ensureAccountDirectory,
  listAllStores,
  loadSnapshot,
  mergeSettings,
  subscribeInvites,
} from '../data'
import { firebaseConfigured, requireAuth } from '../firebase'
import { defaultSettings, type CloudCategory, type CloudShoppingList, type CloudStore, type CloudUserSettings, type Language, type ListInvite, type ThemeMode } from '../models'
import LegacyParityPwa from './ParityPwa'
import {
  bindAccessibleLists,
  completeGoogleRedirect,
  googleSignInForPwa,
  subscribePremiumEntitlement,
  type AccessibleListBinding,
  type PremiumEntitlementState,
} from './services/parityBackend'
import { copy, errorMessage, Field, FilledButton, Notice, OutlinedButton } from './components/ParityUi'
import { AddItemScreen, HomeScreen, InvitationsScreen, SettingsScreen, ShoppingScreen, type CoreCtx } from './screens/CoreScreens'
import AddManyItemsScreen from './screens/AddManyItemsScreen'
import { CreateListScreen, StoresScreen } from './screens/StoreListScreens'
import './parity.css'
import './parity-v2.css'

const EMPTY_PREMIUM: PremiumEntitlementState = { isPremium: false, checking: true, error: null, validUntil: null }
const coreRoutePatterns = [
  /^\/pwa\/?$/,
  /^\/pwa\/create\/?$/,
  /^\/pwa\/stores\/?$/,
  /^\/pwa\/list\/[^/]+$/,
  /^\/pwa\/list\/[^/]+\/add$/,
  /^\/pwa\/list\/[^/]+\/add-legacy$/,
  /^\/pwa\/settings\/?$/,
  /^\/pwa\/invites\/?$/,
]

/**
 * One device-local onboarding gate owns every /pwa deep link. After onboarding, either the new
 * parity shell OR the legacy/decomposed route tree is mounted, never both at the same time. This
 * prevents duplicate owned/shared-list and item listeners while the remaining layout/archive
 * screens are still served by the existing PWA implementation.
 */
export default function PwaParityApp() {
  const location = useLocation()
  const initialLanguage = ((localStorage.getItem('shelfway-language') as Language) || (navigator.language.toLowerCase().startsWith('sv') ? 'sv' : 'en'))
  const [language, setLanguage] = useState<Language>(initialLanguage)
  const [onboarded, setOnboarded] = useState(() => localStorage.getItem('shelfway-onboarding-complete') === '1')

  useEffect(() => {
    localStorage.setItem('shelfway-language', language)
    document.documentElement.lang = language
  }, [language])

  if (!onboarded) {
    return <Onboarding language={language} onLanguage={setLanguage} onComplete={() => {
      localStorage.setItem('shelfway-onboarding-complete', '1')
      setOnboarded(true)
    }} />
  }

  const usesCoreParity = coreRoutePatterns.some((pattern) => pattern.test(location.pathname))
  return usesCoreParity ? <CoreParityApp initialLanguage={language} /> : <LegacyParityPwa />
}

function CoreParityApp({ initialLanguage }: { initialLanguage: Language }) {
  const [language, setLanguageState] = useState<Language>(initialLanguage)
  const [authLoaded, setAuthLoaded] = useState(false)
  const [authMessage, setAuthMessage] = useState('')
  const [user, setUser] = useState<User | null>(null)
  const [settings, setSettings] = useState<CloudUserSettings>(() => defaultSettings(initialLanguage))
  const [lists, setLists] = useState<CloudShoppingList[]>([])
  const [listsReady, setListsReady] = useState(false)
  const [stores, setStores] = useState<CloudStore[]>([])
  const [categories, setCategories] = useState<CloudCategory[]>([])
  const [invites, setInvites] = useState<ListInvite[]>([])
  const [premium, setPremium] = useState<PremiumEntitlementState>(EMPTY_PREMIUM)
  const [accessibleBinding, setAccessibleBinding] = useState<AccessibleListBinding | null>(null)

  useEffect(() => {
    if (!firebaseConfigured) { setAuthLoaded(true); return }
    void completeGoogleRedirect().catch((cause) => setAuthMessage(errorMessage(cause)))
    return onAuthStateChanged(requireAuth(), (next) => {
      setUser(next)
      setAuthLoaded(true)
      if (next) void ensureAccountDirectory(next).catch((cause) => console.warn('Shelfway account directory refresh failed', cause))
    })
  }, [])

  useEffect(() => {
    localStorage.setItem('shelfway-language', language)
    document.documentElement.lang = language
  }, [language])

  useEffect(() => {
    const media = matchMedia('(prefers-color-scheme: dark)')
    const apply = () => {
      const mode = settings.themeMode as ThemeMode
      document.documentElement.dataset.theme = mode === 'system' ? (media.matches ? 'dark' : 'light') : mode
    }
    apply()
    media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [settings.themeMode])

  useEffect(() => {
    if (!user) {
      setLists([])
      setListsReady(false)
      setStores([])
      setCategories([])
      setInvites([])
      setPremium(EMPTY_PREMIUM)
      setAccessibleBinding(null)
      return
    }

    let active = true
    setListsReady(false)
    void loadSnapshot(user.uid).then((snapshot) => {
      if (!active) return
      const remote = snapshot.settings ?? defaultSettings(language)
      const localChecked = localStorage.getItem('shelfway-checked-behavior')
      setSettings({ ...remote, checkedItemBehavior: localChecked || remote.checkedItemBehavior || 'keep_in_category' })
      setCategories(snapshot.categories ?? [])
      if (remote.preferredLanguage === 'sv' || remote.preferredLanguage === 'en') setLanguageState(remote.preferredLanguage)
    }).catch((cause) => console.warn('Shelfway account metadata restore failed', cause))
    void listAllStores().then((rows) => { if (active) setStores(rows) }).catch((cause) => console.warn('Shelfway store restore failed', cause))

    const binding = bindAccessibleLists(user.uid, (rows) => {
      if (!active) return
      setLists(rows)
      setListsReady(true)
    })
    setAccessibleBinding(binding)
    const unsubscribeInvites = subscribeInvites(user.uid, (rows) => { if (active) setInvites(rows) })
    const unsubscribePremium = subscribePremiumEntitlement(user.uid, (state) => { if (active) setPremium(state) })

    return () => {
      active = false
      binding.unsubscribe()
      unsubscribeInvites()
      unsubscribePremium()
      setAccessibleBinding((current) => current === binding ? null : current)
    }
  }, [user?.uid])

  const setLanguage = (next: Language) => {
    setLanguageState(next)
    if (!user) return
    const updated = { ...settings, preferredLanguage: next }
    setSettings(updated)
    void mergeSettings(user.uid, updated, user.email).catch(console.warn)
  }
  const setThemeMode = (mode: ThemeMode) => {
    const updated = { ...settings, themeMode: mode }
    setSettings(updated)
    if (user) void mergeSettings(user.uid, updated, user.email).catch(console.warn)
  }
  const setCheckedBehavior = (behavior: string) => {
    localStorage.setItem('shelfway-checked-behavior', behavior)
    const updated = { ...settings, checkedItemBehavior: behavior }
    setSettings(updated)
    if (user) void mergeSettings(user.uid, updated, user.email).catch(console.warn)
  }

  if (!authLoaded) return <Splash />
  if (!firebaseConfigured) return <ConfigMissing />
  if (!user) return <AuthScreen language={language} initialMessage={authMessage} />

  const ctx: CoreCtx = {
    user,
    language,
    settings,
    lists,
    listsReady,
    stores,
    categories,
    invites,
    premium,
    accessibleBinding,
    setLanguage,
    setThemeMode,
    setCheckedBehavior,
  }

  return <div className="pwa-parity parity-v2-root">
    <Routes>
      <Route path="/pwa/" element={<HomeScreen ctx={ctx} />} />
      <Route path="/pwa/create" element={<CreateListScreen ctx={ctx} />} />
      <Route path="/pwa/stores" element={<StoresScreen ctx={ctx} />} />
      <Route path="/pwa/list/:listId" element={<ShoppingScreen ctx={ctx} />} />
      <Route path="/pwa/list/:listId/add" element={<AddItemScreen ctx={ctx} />} />
      <Route path="/pwa/list/:listId/add-legacy" element={<AddManyItemsScreen ctx={ctx} />} />
      <Route path="/pwa/settings" element={<SettingsScreen ctx={ctx} />} />
      <Route path="/pwa/invites" element={<InvitationsScreen ctx={ctx} />} />
      <Route path="*" element={<Navigate to="/pwa/" replace />} />
    </Routes>
  </div>
}

function Splash() {
  return <div className="pwa-parity parity-splash"><img src="/pwa/icon.svg" alt="" /><strong>SHELFWAY</strong></div>
}

function ConfigMissing() {
  return <div className="pwa-parity parity-auth"><div className="auth-column"><h1>SHELFWAY</h1><p>Firebase configuration is missing for this deployment.</p></div></div>
}

function Onboarding({ language, onLanguage, onComplete }: { language: Language; onLanguage: (language: Language) => void; onComplete: () => void }) {
  const [page, setPage] = useState(0)
  const pages = [
    { icon: <ShoppingCart />, en: ['Shop the smart way', 'SHELFWAY keeps your list simple and puts items in the order you meet them in the store.'], sv: ['Handla smartare', 'SHELFWAY håller listan enkel och lägger varorna i den ordning du möter dem i butiken.'] },
    { icon: <RouteIcon />, en: ['Choose a store. Follow its route.', 'Use an existing shopping order or create your own when a store is missing one.'], sv: ['Välj butik. Följ butikens väg.', 'Använd en befintlig butiksordning eller skapa din egen när en butik saknar en.'] },
    { icon: <Cloud />, en: ['Your lists, everywhere', 'Your account keeps lists in the cloud, syncs devices and lets you shop together in the same list.'], sv: ['Dina listor, överallt', 'Ditt konto sparar listor i molnet, synkar enheter och låter er handla tillsammans i samma lista.'] },
    { icon: <Crown />, en: ['Unlock more with SHELFWAY Premium', 'Get smarter community item suggestions and an ad-free shopping experience. Choose a monthly or yearly plan after signing in.'], sv: ['Få mer med SHELFWAY Premium', 'Få smartare varuförslag från communityn och handla helt utan annonser. Välj månads- eller årsplan efter att du loggat in.'] },
  ]
  const current = pages[page]
  const text = language === 'sv' ? current.sv : current.en
  return <div className="pwa-parity parity-onboarding parity-onboarding-v2">
    <div className="onboarding-language"><button type="button" className={`filter-chip ${language === 'en' ? 'selected' : ''}`} onClick={() => onLanguage('en')}>EN</button><button type="button" className={`filter-chip ${language === 'sv' ? 'selected' : ''}`} onClick={() => onLanguage('sv')}>SV</button></div>
    <div className="onboarding-main"><div className="onboarding-icon">{current.icon}</div><h1>SHELFWAY</h1><div className="tagline">Shop the smart way</div><h2>{text[0]}</h2><p>{text[1]}</p></div>
    <div className="onboarding-footer"><div className="dots">{pages.map((_, index) => <span key={index} className={index === page ? 'active' : ''}>{index === page ? '●' : '○'}</span>)}</div><FilledButton className="full" onClick={() => page < pages.length - 1 ? setPage(page + 1) : onComplete()}>{page < pages.length - 1 ? copy(language, 'Next', 'Nästa') : copy(language, 'Continue to sign in', 'Fortsätt till inloggning')}</FilledButton></div>
  </div>
}

function AuthScreen({ language, initialMessage = '' }: { language: Language; initialMessage?: string }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState(initialMessage)
  const [busy, setBusy] = useState(false)
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true)
    setMessage('')
    try { await action() } catch (cause) { setMessage(errorMessage(cause)) } finally { setBusy(false) }
  }
  return <div className="pwa-parity parity-auth"><div className="auth-column">
    <Cloud className="hero-icon" /><h1>SHELFWAY</h1>
    {message && <Notice error>{message}</Notice>}
    <h2>{copy(language, 'Sign in to continue', 'Logga in för att fortsätta')}</h2>
    <p className="muted">{copy(language, 'Your account keeps lists in the cloud, syncs them across devices and enables shared lists.', 'Ditt konto sparar listor i molnet, synkar dem mellan enheter och möjliggör delade listor.')}</p>
    <Field label={copy(language, 'Email', 'E-post')}><input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></Field>
    <Field label={copy(language, 'Password', 'Lösenord')}><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></Field>
    <FilledButton className="full" disabled={busy || !email.trim() || password.length < 6} onClick={() => void run(() => emailSignIn(email, password))}>{copy(language, 'Sign in', 'Logga in')}</FilledButton>
    <OutlinedButton className="full" disabled={busy || !email.trim() || password.length < 6} onClick={() => void run(() => emailSignUp(email, password))}>{copy(language, 'Create account', 'Skapa konto')}</OutlinedButton>
    <div className="or-divider"><span /><em>{copy(language, 'or', 'eller')}</em><span /></div>
    <OutlinedButton className="full" disabled={busy} onClick={() => void run(() => googleSignInForPwa())}>{copy(language, 'Continue with Google', 'Fortsätt med Google')}</OutlinedButton>
  </div></div>
}
